import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Checkbox,
  Grid,
  TableCell,
  TableRow,
  useTheme,
} from "@mui/material";
import { CompletionBar } from "~/components/Completion/CompletionBar";
import { ComplianceTable } from "~/components/Completion/ComplianceTable";
import { FilterBar } from "~/components/FilterBar/FilterBar";
import {
  ComplianceAssetOrderField,
  ExceptionMutationAction,
  GetComplianceControlAssetsDocument,
  OrderDirection,
  useGetComplianceControlAssetsQuery,
} from "~/operations";
import { useSearch } from "~/components/search/useSearch";
import { EmptyState } from "~/components/empty-state/empty-state";
import { useGroupingButton } from "~/components/grouping-button/useGroupingButton";
import { GroupingButton } from "~/components/grouping-button";
import { useExceptions } from "~/components/exceptions/use-exceptions";
import { INITIAL_PAGE_RANGE } from "~/components/pagination";
import { useCanApplyExceptionMutation } from "~/lib/iam/actions/mondoo/policy/ExtendedHub/ApplyExceptionMutation";
import { Chip } from "~/components/chip";
import { Block as BlockIcon } from "@mui/icons-material";
import { mapSelectedEntitiesToString } from "~/components/exceptions/utils/mapSelectedEntitiesToString";
import { OutOfScopeChip } from "~/components/exceptions/out-of-scope-chip";
import { FormatRelativeDate, FormatTime } from "~/lib/date";
import { Loading, LoadingFailed } from "~/components/loading";
import { useControlContext } from "~/pages/compliance/Control/ComplianceControl";
import { setDocumentTitle } from "~/lib/commonUtils";
import { TriageToolbarButtonType } from "~/components/TriageToolbar/types";
import { useTriageToolbar } from "~/providers/triage-toolbar";
import { useExceptionsButtons } from "~/components/exceptions/use-exceptions-buttons";
import { useSelection } from "~/hooks/useSelection";
import { SelectedEntityWithException } from "~/components/exceptions/types";
import { Header } from "~/types/table";
import { MoonIcon } from "~/icons";

export type ComplianceControlAssetsProps = {};

export function ComplianceControlAssets({}: ComplianceControlAssetsProps) {
  const { space, controlMrn, frameworkMrn, frameworkName, control } =
    useControlContext();
  const search = useSearch();
  const { toggleSelectedGroupingButton, selectedGroupingButton } =
    useGroupingButton();
  const theme = useTheme();
  const [pageItems, setPageItems] = useState(INITIAL_PAGE_RANGE);
  const navigate = useNavigate();
  const triageToolbar = useTriageToolbar();

  const hasApplyExceptionPermission = useCanApplyExceptionMutation();

  const {
    isGroupChecked,
    isGroupIndeterminate,
    onGroupCheckChange,
    selectedEntities: selectedAssets,
    setSelectedEntities: setSelectedAssets,
    handleNodeClick,
    handleNodeChange,
    handleCancelClick,
  } = useSelection<SelectedEntityWithException>();

  const {
    exceptionsButtonsAvailability,
    exceptionsButtonsVisibility,
    exceptionsButtonsTooltipText,
  } = useExceptionsButtons({
    selectedEntities: selectedAssets,
    target: "asset",
    showExceptionButtons: false,
    showScopeButtons: true,
  });

  useEffect(() => {
    if (selectedAssets.length > 0) {
      triageToolbar.show({
        buttons: [
          ...(exceptionsButtonsVisibility.SET_OUT_OF_SCOPE
            ? [
                {
                  type: TriageToolbarButtonType.SetOutOfScope,
                  onClick: () => handleSetScopeForMultipleScopes(false),
                  disabled: !exceptionsButtonsAvailability.SET_OUT_OF_SCOPE,
                  tooltip: exceptionsButtonsTooltipText.SET_OUT_OF_SCOPE,
                },
              ]
            : []),
          ...(exceptionsButtonsVisibility.SET_IN_SCOPE
            ? [
                {
                  type: TriageToolbarButtonType.SetInScope,
                  onClick: async () => {
                    await handleRemoveExceptionGroups({
                      exceptionMrns: selectedAssets
                        .map((a) => a.exception?.exceptionMrn)
                        .filter((mrn): mrn is string => Boolean(mrn)),
                    });
                    setSelectedAssets([]);
                  },
                  disabled: !exceptionsButtonsAvailability.SET_IN_SCOPE,
                  tooltip: exceptionsButtonsTooltipText.SET_IN_SCOPE,
                },
              ]
            : []),
        ],
        payload: {
          count: selectedAssets.length,
          totalCount: assets?.totalCount || 0,
          onClose: handleCancelClick,
        },
      });
    } else {
      triageToolbar.hide();
    }

    return () => {
      triageToolbar.hide();
    };
  }, [selectedAssets]);

  const {
    data: { complianceControl } = {},
    error,
    loading,
    fetchMore,
  } = useGetComplianceControlAssetsQuery({
    variables: {
      input: {
        controlMrn,
        frameworkMrn,
        scopeMrn: space.mrn,
      },
      assetsInput: {
        after: null,
        first: null,
        orderBy: {
          direction: OrderDirection.Asc,
          field: ComplianceAssetOrderField.Name,
        },
        query: search.searchFilters.join(" "),
        groups: selectedGroupingButton
          ? [{ groupType: selectedGroupingButton }]
          : undefined,
      },
    },
  });

  const { handleSetScopeForMultipleScopes, handleRemoveExceptionGroups } =
    useExceptions({
      onSetException: () => {
        setSelectedAssets([]);
      },
      // TBD when backend is ready
      scopeMrns: mapSelectedEntitiesToString(selectedAssets),
      controlMrns: [controlMrn],
      multipleScopes: true,
      refetchQueries: [
        {
          query: GetComplianceControlAssetsDocument,
          variables: {
            input: {
              controlMrn,
              frameworkMrn,
              scopeMrn: space.mrn,
            },
            assetsInput: {
              after: null,
              first: null,
              orderBy: {
                direction: OrderDirection.Asc,
                field: ComplianceAssetOrderField.Name,
              },
              query: search.searchFilters.join(" "),
              groups: selectedGroupingButton
                ? [{ groupType: selectedGroupingButton }]
                : undefined,
            },
          },
        },
      ],
    });

  const { assets } = complianceControl || {};
  const { assetGroupStatistics } = control || {};

  const targetPaginatedGroup = (assets?.edges || [])
    .slice(pageItems.from, pageItems.to)
    .map((edge) => ({
      mrn: edge.node?.asset?.mrn as string,
      exception: edge.node?.exception,
      groupId: edge.node?.exception?.id,
    }));

  setDocumentTitle([
    "Assets",
    control.title,
    frameworkName,
    "Compliance",
    space.name,
  ]);

  if (loading && !assets) {
    return <Loading what="assets" />;
  }

  if (error) {
    return <LoadingFailed what="assets" />;
  }

  const assetsEdges = assets?.edges ?? [];
  const assetsArray = assetsEdges.map((edge) => edge?.node);
  const groups = assetGroupStatistics?.filter((group) => group.count > 0) || [];

  const tableHeaders: Header[] = [
    hasApplyExceptionPermission
      ? {
          id: "SELECT",
          label: (
            <Checkbox
              checked={isGroupChecked(targetPaginatedGroup)}
              indeterminate={isGroupIndeterminate(targetPaginatedGroup)}
              onChange={(event) =>
                onGroupCheckChange(event, targetPaginatedGroup)
              }
            />
          ),
          sortable: false,
        }
      : undefined,
    {
      id: "ASSET_NAME",
      label: "Asset name",
    },
    {
      id: "PLATFORM",
      label: "Platform",
    },
    {
      id: "COMPLETION",
      label: "Completion",
    },
    {
      id: "LAST_UPDATED",
      label: "Last updated",
    },
  ].flatMap((h) => h ?? []);

  if (!assetsArray.length) {
    return <EmptyState contentType="control-assets" scope={space} />;
  }

  const handleAssetClick = (assetId: string | undefined) => {
    if (!assetId) return;

    navigate(`/space/inventory/${assetId}/overview?spaceId=${space.id}`);
  };

  return (
    <Box>
      {groups.length > 0 && (
        <Grid container spacing={2} sx={{ my: 5 }}>
          {groups.map((group) => {
            return (
              <Grid
                key={group.groupType}
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4,
                }}
              >
                <GroupingButton
                  {...{ group }}
                  onClick={() => toggleSelectedGroupingButton(group.groupType)}
                />
              </Grid>
            );
          })}
        </Grid>
      )}
      <FilterBar
        searchId="compliance-control-assets"
        placeholder="compliance_assets"
        search={search}
      />
      <ComplianceTable
        tableHeaders={tableHeaders}
        defaultActiveColumn={ComplianceAssetOrderField.Name}
        paginationProps={{
          fetchMore,
          totalCount: assets?.totalCount || 0,
          pageInfo: assets?.pageInfo,
          setPageItems: setPageItems,
        }}
      >
        {assetsArray.slice(pageItems.from, pageItems.to).map((asset) => {
          const updatedAt = asset?.asset?.updatedAt;
          const formattedUpdatedAt = updatedAt
            ? `${FormatRelativeDate(updatedAt)} - ${FormatTime(updatedAt)}`
            : "";
          const isModified =
            asset?.exception &&
            asset?.exception?.action !== ExceptionMutationAction.Enable;
          const isDisabled =
            asset?.exception?.action === ExceptionMutationAction.Disable;
          const isRiskAccepted =
            asset?.exception?.action === ExceptionMutationAction.RiskAccepted;
          const isOutOfScope =
            asset?.exception?.action === ExceptionMutationAction.OutOfScope;

          return (
            <TableRow
              onClick={() => handleAssetClick(asset?.asset?.id)}
              key={asset?.asset?.id}
              sx={{
                "& .MuiTableCell-root": { fontWeight: 400 },
              }}
            >
              {hasApplyExceptionPermission && (
                <TableCell sx={{ opacity: isModified ? 0.5 : 1 }}>
                  <Checkbox
                    onClick={(e) => handleNodeClick(e)}
                    onChange={(e, changed) =>
                      handleNodeChange(e, changed, {
                        mrn: asset?.asset?.mrn as string,
                        exception: asset?.exception,
                      })
                    }
                    checked={Boolean(
                      selectedAssets.find(
                        (selectedCheck) =>
                          asset?.asset?.mrn === selectedCheck.mrn,
                      ),
                    )}
                  />
                </TableCell>
              )}
              <TableCell sx={{ width: "30%", opacity: isModified ? 0.5 : 1 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  {isRiskAccepted && (
                    <Chip label="Snoozed" icon={<MoonIcon />} size="small" />
                  )}
                  {isDisabled && (
                    <Chip label="Disabled" icon={<BlockIcon />} size="small" />
                  )}
                  {isOutOfScope && (
                    <OutOfScopeChip
                      author={asset?.exception?.author.name}
                      date={asset?.exception?.createdAt}
                    />
                  )}
                  {asset?.asset?.name}
                </Box>
              </TableCell>
              <TableCell sx={{ width: "15%", opacity: isModified ? 0.5 : 1 }}>
                {asset?.asset?.platform?.name}
              </TableCell>
              <TableCell sx={{ width: "15%", opacity: isModified ? 0.5 : 1 }}>
                <CompletionBar
                  percent={asset?.compliancePercentage || 0}
                  height={theme.spacing(1)}
                  tooltip={`Completion: ${asset?.compliancePercentage}%`}
                />
              </TableCell>
              <TableCell
                sx={{
                  width: "20%",
                  fontSize: (theme) => theme.spacing(1.25),
                  opacity: isModified ? 0.5 : 1,
                }}
              >
                {formattedUpdatedAt}
              </TableCell>
            </TableRow>
          );
        })}
      </ComplianceTable>
    </Box>
  );
}
